import { AppliedDiscountType, DiscountScope, isAppliedDiscountType } from './types';
import { readWriteOffActors } from './writeOffActors';

/**
 * A discount that is ON an order or one of its lines.
 *
 * `amountApplied` is the ONLY money authority here. `value` is how the
 * discount was EXPRESSED (10 for "10%", 5 for "$5 off", 100 for a comp) and
 * exists so a reader can format it; no money path re-derives an amount from
 * it. See `AppliedDiscountType` in `./types` for why a comp is a first-class
 * type and not a `fixedAmount` wearing a name.
 */
export interface IAppliedDiscount {
    readonly discountId?: string;
    readonly name: string;
    readonly type: AppliedDiscountType;
    readonly value: number;
    readonly scope: DiscountScope;
    readonly amountApplied: number;
    readonly reason?: string;
    /**
     * The employee who APPROVED this discount — a manager's id for a comp or
     * any other approval-gated giveaway.
     *
     * Optional, and optional deliberately: every document written before this
     * field existed has none, and inventing one would name a manager nobody
     * proved was there. It is attribution, never authorization — a server
     * route must decide "may this happen" from the verified operator
     * (`request.operator`), exactly as the cash void does
     * (docs/PAYMENT_REVERSAL_ARCHITECTURE.md §Cash void), and may record this
     * afterwards.
     *
     * Its sibling `reason` is the STORY ("sent back" and "manager courtesy"
     * are different stories with the same dollar value); this is the WHO.
     */
    readonly authorizedBy?: string;
    /**
     * The employee signed in on the till who APPLIED it — a different person
     * from {@link authorizedBy} whenever a manager's PIN approved a server's
     * comp (owner, 2026-09-22). Attribution, like `authorizedBy`.
     */
    readonly appliedBy?: string;
    readonly appliedByName?: string;
    readonly authorizedByName?: string;
    /** A manager typed their PIN for it (a step-up). */
    readonly approvedWithPin?: boolean;
    /**
     * WHEN it was applied — an ISO-8601 UTC instant string, written by the
     * till. Who, who approved and why are not an audit record without when.
     */
    readonly appliedAt?: string;
}

export interface IAppliedDiscountData {
    discountId?: string;
    name: string;
    type?: AppliedDiscountType;
    value?: number;
    scope?: DiscountScope;
    amountApplied?: number;
    reason?: string;
    authorizedBy?: string;
    appliedBy?: string;
    appliedByName?: string;
    authorizedByName?: string;
    approvedWithPin?: boolean;
    appliedAt?: string;
}

export class AppliedDiscount implements IAppliedDiscount {
    readonly discountId?: string;
    readonly name: string;
    readonly type: AppliedDiscountType;
    readonly value: number;
    readonly scope: DiscountScope;
    readonly amountApplied: number;
    readonly reason?: string;
    readonly authorizedBy?: string;
    readonly appliedBy?: string;
    readonly appliedByName?: string;
    readonly authorizedByName?: string;
    readonly approvedWithPin?: boolean;
    readonly appliedAt?: string;

    constructor(data: IAppliedDiscountData) {
        this.discountId = data.discountId;
        this.name = data.name;
        this.type = data.type ?? 'fixedAmount';
        this.value = data.value ?? 0;
        this.scope = data.scope ?? 'order';
        this.amountApplied = data.amountApplied ?? 0;
        this.reason = data.reason;
        this.authorizedBy = data.authorizedBy;
        this.appliedBy = data.appliedBy;
        this.appliedByName = data.appliedByName;
        this.authorizedByName = data.authorizedByName;
        this.approvedWithPin = data.approvedWithPin;
        this.appliedAt = data.appliedAt;
    }

    /** A giveaway rather than an agreed price — see `AppliedDiscountType`. */
    get isComp(): boolean {
        return this.type === 'comp';
    }

    toJSON(): IAppliedDiscount {
        return {
            discountId: this.discountId,
            name: this.name,
            type: this.type,
            value: this.value,
            scope: this.scope,
            amountApplied: this.amountApplied,
            reason: this.reason,
            authorizedBy: this.authorizedBy,
            appliedBy: this.appliedBy,
            appliedByName: this.appliedByName,
            authorizedByName: this.authorizedByName,
            approvedWithPin: this.approvedWithPin,
            appliedAt: this.appliedAt,
        };
    }

    static fromJSON(data: Record<string, unknown>): AppliedDiscount {
        if (typeof data.name !== 'string') {
            throw new Error("AppliedDiscount.fromJSON: name must be a string");
        }

        // 🔴 A REJECTED TYPE IS NOT A REJECTED FIELD. This throw runs inside
        // `OrderItem.fromJSON` → `Order.fromJSON` → `snapshot.docs.map(...)`,
        // so a type this build does not know takes down the whole read, not
        // one line. `comp` used to land here, from every POS that ever comped
        // an item. Keep the list in `APPLIED_DISCOUNT_TYPES` — one writer.
        //
        // A FALSY type stays "unset" and takes the default, exactly as before:
        // widening the check to reject `''` as well would newly throw on
        // documents this parser has always accepted, which is the same defect
        // pointed the other way.
        const rawType = data.type;
        if (rawType && !isAppliedDiscountType(rawType)) {
            throw new Error("AppliedDiscount.fromJSON: invalid discount type");
        }
        const type = (rawType || undefined) as AppliedDiscountType | undefined;

        const scope = data.scope as DiscountScope;
        if (scope && !['order', 'item'].includes(scope)) {
            throw new Error("AppliedDiscount.fromJSON: invalid discount scope");
        }

        return new AppliedDiscount({
            discountId: data.discountId as string | undefined,
            name: data.name,
            type: type ?? 'fixedAmount',
            value: typeof data.value === 'number' ? data.value : 0,
            scope: scope ?? 'order',
            amountApplied: typeof data.amountApplied === 'number' ? data.amountApplied : 0,
            reason: data.reason as string | undefined,
            appliedAt: typeof data.appliedAt === 'string' ? data.appliedAt : undefined,
            authorizedBy: typeof data.authorizedBy === 'string' ? data.authorizedBy : undefined,
            // The till writes the performer as `appliedBy` on a discount (it
            // is `performedBy` everywhere else); the rest are the shared keys.
            appliedBy: readWriteOffActors({ performedBy: data.appliedBy }).performedBy,
            appliedByName: readWriteOffActors({ performedByName: data.appliedByName }).performedByName,
            ...(() => {
                const a = readWriteOffActors(data);
                return {
                    authorizedByName: a.authorizedByName,
                    approvedWithPin: a.approvedWithPin,
                };
            })(),
        });
    }
}
